import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw,
  Users,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Archive,
  ArrowRightCircle,
  Info,
  Sparkles,
  ShieldCheck,
  History,
  Undo2,
  Layers,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const WADAH_COLORS = {
  PERIA: { bg: "bg-[#276AA3]/8", ring: "ring-[#276AA3]/20", text: "text-[#276AA3]", accent: "#276AA3" },
  PERNITA: { bg: "bg-[#8A5FA3]/8", ring: "ring-[#8A5FA3]/20", text: "text-[#8A5FA3]", accent: "#8A5FA3" },
  PEREDA: { bg: "bg-[#3C8C4A]/8", ring: "ring-[#3C8C4A]/20", text: "text-[#3C8C4A]", accent: "#3C8C4A" },
  PERSEMI: { bg: "bg-[#C8473D]/8", ring: "ring-[#C8473D]/20", text: "text-[#C8473D]", accent: "#C8473D" },
  KOMISI: { bg: "bg-[#B58B00]/8", ring: "ring-[#B58B00]/20", text: "text-[#B58B00]", accent: "#B58B00" },
};

const CONFIRM = "SINKRONKAN WADAH KLASIS";
const RESTORE_CONFIRM = "KEMBALIKAN ARSIP";

export default function WadahSyncPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [archiveAnggota, setArchiveAnggota] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const [{ data: prev }, { data: hist }] = await Promise.all([
        api.get("/admin/wadah-sync/preview"),
        api.get("/admin/wadah-sync/archive-log?limit=10").catch(() => ({ data: { batches: [] } })),
      ]);
      setPreview(prev);
      setHistory(hist.batches || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
  }, []);

  const applySync = async () => {
    if (confirmText.trim() !== CONFIRM) {
      toast.error(`Ketik dengan tepat: ${CONFIRM}`);
      return;
    }
    if (
      !window.confirm(
        `Anda akan menerapkan sinkronisasi struktur wadah.\n\n` +
          `- ${preview?.summary?.to_upsert || 0} record baru akan ditambahkan\n` +
          `- ${preview?.summary?.to_archive || 0} record lama akan diarsipkan\n` +
          `- ${preview?.summary?.matched || 0} record sudah sesuai (dibiarkan)\n\n` +
          (archiveAnggota ? `⚠️ Anggota wadah juga akan diarsipkan.\n\n` : "") +
          `Lanjutkan?`
      )
    )
      return;

    setApplying(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/wadah-sync/apply", {
        orgs: null,
        archive_anggota: archiveAnggota,
        confirm_phrase: confirmText.trim(),
      });
      setResult(data);
      toast.success(
        `Sinkronisasi selesai: ${data.summary.inserted} ditambahkan, ${data.summary.archived} diarsipkan`
      );
      setConfirmText("");
      loadPreview();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setApplying(false);
    }
  };

  const restoreBatch = async (batchId) => {
    const phrase = window.prompt(`Ketik "${RESTORE_CONFIRM}" untuk mengembalikan arsip ini:`);
    if (!phrase || phrase.trim() !== RESTORE_CONFIRM) {
      toast.error("Frasa konfirmasi tidak sesuai");
      return;
    }
    try {
      const { data } = await api.post(`/admin/wadah-sync/restore/${batchId}`, {
        confirm_phrase: phrase.trim(),
      });
      toast.success(
        `Dikembalikan: ${data.restored_positions} pengurus, ${data.restored_anggota} anggota`
      );
      loadPreview();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const summary = preview?.summary || { orgs: 0, matched: 0, to_upsert: 0, to_archive: 0 };
  const hasChanges = summary.to_upsert > 0 || summary.to_archive > 0;

  return (
    <Card className="overflow-hidden border-primary/20 shadow-lg" data-testid="wadah-sync-panel">
      <CardHeader className="bg-gradient-to-r from-primary/8 via-primary/4 to-transparent border-b border-primary/10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/10 ring-2 ring-primary/20 flex items-center justify-center shrink-0">
              <RefreshCw className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                Sinkronisasi Struktur Wadah
                <Badge variant="outline" className="text-[0.65rem] font-bold border-primary/30">
                  <Sparkles className="h-2.5 w-2.5 mr-1" /> SAFE
                </Badge>
              </CardTitle>
              <CardDescription className="mt-1 max-w-xl">
                Terapkan struktur pengurus canonical (PERIA, PERNITA, PEREDA, PERSEMI, KOMISI) ke
                database Production. Idempotent, non-destructive, dengan audit log otomatis.
              </CardDescription>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadPreview}
            disabled={loading}
            data-testid="btn-refresh-preview"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* SUMMARY */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            label="Wadah/Komisi"
            value={summary.orgs}
            icon={Layers}
            testId="sync-stat-orgs"
            hint="Total org yang di-scan"
            color="text-primary"
          />
          <StatCard
            label="Sesuai (matched)"
            value={summary.matched}
            icon={CheckCircle2}
            testId="sync-stat-matched"
            hint="Record yang sudah benar"
            color="text-emerald-600"
          />
          <StatCard
            label="Akan Ditambah"
            value={summary.to_upsert}
            icon={ArrowRightCircle}
            testId="sync-stat-upsert"
            hint="Record baru/typo diperbaiki"
            color="text-amber-600"
            pulse={summary.to_upsert > 0}
          />
          <StatCard
            label="Akan Diarsipkan"
            value={summary.to_archive}
            icon={Archive}
            testId="sync-stat-archive"
            hint="Ke `positions_archive`"
            color="text-rose-600"
          />
        </div>

        {/* PER-ORG DIFF */}
        {preview && (
          <div className="space-y-2" data-testid="sync-per-org-diff">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Info className="h-3.5 w-3.5" /> Rincian per Wadah / Komisi
            </div>
            {Object.entries(preview.details).map(([org, d]) => {
              const col = WADAH_COLORS[org] || WADAH_COLORS.KOMISI;
              const expanded = expandedOrg === org;
              const changes = d.to_upsert.length + d.to_archive.length;
              const hasChange = changes > 0;

              return (
                <div
                  key={org}
                  className={`rounded-xl border transition-all overflow-hidden ${
                    hasChange ? `${col.ring} ring-1 shadow-sm` : "border-border/60"
                  } ${col.bg}`}
                  data-testid={`sync-org-${org}`}
                >
                  <button
                    className="w-full flex items-center justify-between p-3 hover:bg-white/40 transition-colors"
                    onClick={() => setExpandedOrg(expanded ? null : org)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`font-heading font-black text-base ${col.text}`}>{org}</div>
                      <Badge variant="outline" className="text-[0.65rem]">
                        {d.existing_total} → {d.canonical_total}
                      </Badge>
                      {hasChange ? (
                        <Badge className="text-[0.65rem] bg-amber-500 hover:bg-amber-600">
                          {changes} perubahan
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[0.65rem] border-emerald-500/40 text-emerald-700">
                          <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Sinkron
                        </Badge>
                      )}
                    </div>
                    {expanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>

                  {expanded && (
                    <div className="px-3 pb-3 space-y-3 text-sm bg-white/60 border-t border-border/40">
                      {/* Upsert list */}
                      {d.to_upsert.length > 0 && (
                        <div>
                          <div className="text-[0.7rem] uppercase font-bold tracking-widest text-amber-700 flex items-center gap-1 mt-2">
                            <ArrowRightCircle className="h-3 w-3" /> Ditambahkan / Diperbaiki (
                            {d.to_upsert.length})
                          </div>
                          <div className="mt-1.5 space-y-1">
                            {d.to_upsert.map((u, i) => (
                              <div
                                key={i}
                                className="bg-amber-50/70 border border-amber-200 rounded-md px-2.5 py-1.5 flex items-start gap-2 text-xs"
                              >
                                <span className="text-amber-700 font-bold">＋</span>
                                <div className="flex-1">
                                  <div className="font-semibold">{u.canonical.title}</div>
                                  <div className="text-emerald-700">
                                    → <strong>{u.canonical.person_name}</strong>
                                  </div>
                                  {u.fuzzy_matches?.[0] && (
                                    <div className="text-muted-foreground line-through text-[0.7rem] mt-0.5">
                                      typo lama: {u.fuzzy_matches[0].person_name}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Archive list */}
                      {d.to_archive.length > 0 && (
                        <div>
                          <div className="text-[0.7rem] uppercase font-bold tracking-widest text-rose-700 flex items-center gap-1">
                            <Archive className="h-3 w-3" /> Diarsipkan ({d.to_archive.length})
                          </div>
                          <div className="mt-1.5 space-y-1">
                            {d.to_archive.map((a, i) => (
                              <div
                                key={i}
                                className="bg-rose-50/70 border border-rose-200 rounded-md px-2.5 py-1.5 flex items-start gap-2 text-xs"
                              >
                                <span className="text-rose-700 font-bold">➖</span>
                                <div className="flex-1">
                                  <div className="font-semibold">{a.title || "-"}</div>
                                  <div className="text-muted-foreground">
                                    {a.person_name || "-"}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {d.to_upsert.length === 0 && d.to_archive.length === 0 && (
                        <div className="text-xs text-emerald-700 flex items-center gap-2 py-2">
                          <CheckCircle2 className="h-4 w-4" />
                          Struktur {org} sudah sesuai canonical.
                        </div>
                      )}

                      {/* Anggota count for wadah */}
                      {["PERIA", "PERNITA", "PEREDA", "PERSEMI"].includes(org) && (
                        <div className="border-t border-border/40 pt-2 flex items-center gap-2 text-xs text-muted-foreground">
                          <Users className="h-3 w-3" />
                          Anggota wadah saat ini:{" "}
                          <strong className="text-foreground">
                            {preview.anggota_by_wadah?.[org] ?? 0}
                          </strong>
                          {archiveAnggota && (preview.anggota_by_wadah?.[org] ?? 0) > 0 && (
                            <Badge variant="destructive" className="text-[0.6rem] ml-2">
                              akan diarsipkan
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ANGGOTA ARCHIVE OPTION */}
        <div className="bg-muted/40 rounded-xl p-4 border border-border/60">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={archiveAnggota}
              onChange={(e) => setArchiveAnggota(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
              data-testid="checkbox-archive-anggota"
            />
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Kosongkan Anggota Wadah (arsipkan semua)
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Pindahkan semua record `anggota` (wadah=PERIA/PERNITA/PEREDA/PERSEMI) ke koleksi{" "}
                <code>anggota_archive</code>. Berguna untuk fresh start setelah migrasi. Bisa
                dikembalikan lewat riwayat arsip.
              </div>
            </div>
          </label>
        </div>

        {/* CONFIRMATION & APPLY */}
        <div className="border-t border-border pt-4 space-y-3">
          <div className="flex items-start gap-2 text-xs bg-primary/5 border border-primary/20 rounded-md p-3">
            <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div className="text-foreground/80">
              <strong>Non-destructive:</strong> Record lama diarsipkan ke{" "}
              <code>positions_archive</code>, tidak dihapus. Bisa dikembalikan kapan saja lewat
              tombol <strong>Restore</strong> di riwayat arsip.
            </div>
          </div>

          {hasChanges ? (
            <>
              <Label htmlFor="wadah-sync-confirm" className="text-sm font-semibold">
                Ketik <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs">{CONFIRM}</code>{" "}
                untuk konfirmasi
              </Label>
              <Input
                id="wadah-sync-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM}
                className="font-mono"
                data-testid="input-wadah-sync-confirm"
              />
              <Button
                onClick={applySync}
                disabled={applying || confirmText.trim() !== CONFIRM}
                className="w-full sm:w-auto rounded-full"
                data-testid="btn-wadah-sync-apply"
                style={{ backgroundColor: "#276AA3" }}
              >
                {applying ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {applying ? "Menerapkan sinkronisasi…" : "Terapkan Sinkronisasi"}
              </Button>
            </>
          ) : (
            <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md p-4 text-emerald-900 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5" />
              <div>
                <div className="font-bold">Semua struktur wadah sudah sinkron dengan canonical.</div>
                <div className="text-xs mt-0.5 text-emerald-800/80">
                  Tidak ada perubahan yang perlu diterapkan. Data siap tampil di halaman Bidang &
                  Komisi.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RESULT */}
        {result && (
          <div className="border-t border-border pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5" /> Hasil Sinkronisasi Terakhir
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <MiniStat label="Sesuai" value={result.summary.matched} />
              <MiniStat label="Ditambah" value={result.summary.inserted} color="text-emerald-700" />
              <MiniStat label="Diarsipkan" value={result.summary.archived} color="text-rose-700" />
              <MiniStat
                label="Anggota Arsip"
                value={result.summary.anggota_archived}
                color="text-amber-700"
              />
            </div>
            <div className="text-[0.7rem] mt-2 text-muted-foreground font-mono">
              batch_id: {result.batch_id}
            </div>
          </div>
        )}

        {/* HISTORY */}
        {history.length > 0 && (
          <div className="border-t border-border pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2 mb-3">
              <History className="h-3.5 w-3.5" /> Riwayat Arsip ({history.length})
            </div>
            <div className="space-y-2">
              {history.map((b) => (
                <div
                  key={b.batch_id}
                  className="flex items-center justify-between bg-muted/40 rounded-lg p-2.5 border border-border/60"
                  data-testid={`archive-batch-${b.batch_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold truncate">
                      {b.count} posisi + {b.anggota_archived_count || 0} anggota
                    </div>
                    <div className="text-[0.65rem] text-muted-foreground truncate">
                      {b.sample?.reason || "—"} · {b.last_time?.slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full text-xs h-7 shrink-0 ml-2"
                    onClick={() => restoreBatch(b.batch_id)}
                    data-testid={`btn-restore-${b.batch_id}`}
                  >
                    <Undo2 className="h-3 w-3 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value, icon: Icon, testId, hint, color = "text-primary", pulse }) {
  return (
    <div
      className={`bg-white rounded-xl p-3 border border-border/60 shadow-sm ${
        pulse ? "ring-2 ring-amber-200 animate-pulse-subtle" : ""
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="text-[0.65rem] uppercase tracking-widest font-bold text-muted-foreground">
          {label}
        </div>
        <Icon className={`h-3.5 w-3.5 ${color}`} />
      </div>
      <div className={`text-2xl font-black mt-1 ${color}`}>{value ?? 0}</div>
      <div className="text-[0.65rem] text-muted-foreground mt-0.5">{hint}</div>
    </div>
  );
}

function MiniStat({ label, value, color = "text-foreground" }) {
  return (
    <div className="bg-muted/30 rounded-md p-2 border border-border/60">
      <div className="text-[0.6rem] uppercase tracking-widest text-muted-foreground font-bold">
        {label}
      </div>
      <div className={`font-black text-lg ${color}`}>{value ?? 0}</div>
    </div>
  );
}
